/// <amd-module name="Feed/Core/Redux/FeedHub/FeedHubContracts" />
import * as React from "react";

import { Uri } from "VSS/Platform/Util/Url";
import { ITTIAwareState } from "VSS/Redux/Modules/TTIMonitor";

import { Feed, FeedView } from "Feed/Common/Contracts/Feed";
import { FeedMessage, UpstreamHealthStatus, WebPageData } from "Feed/Common/Contracts/WebPage";
import { IError } from "Feed/Common/Types/IError";
import { IFeedNavigationAwareState } from "Feed/Core/Redux/Navigation";
import { IPackageVersion } from "Feed/Core/Types";

/** Top-level business data that may be utilized by many modules.  */
export interface IFeedHubDataState {
    /**
     * Dictionary of FeedId: IFeedData, all the data we have loaded about the feed
     */
    feeds: { [key: string]: IFeedData };

    /** The selected feed (if any) */
    selectedFeedId?: string;

    /** The selected view (if any) */
    selectedViewId?: string;

    /**
     * Base URI for the packaging service
     */
    packagingUrl: string;
    /**
     * Uri for UI to generate a user session token (PAT)
     */
    sessionTokenUri: Uri;
}

export interface IFeedData {
    feed: Feed;

    feedMessages?: FeedMessage[] | undefined;

    /**
     * Dictionary of ViewId : FeedView
     * TODO: add support for add/delete views
     */
    views: { [key: string]: FeedView } | undefined;

    /**
     * If the user doesn't have access to the base feed, default to this view.
     * value is undefined if the user does have access to the base feed.
     */
    accessibleViewId?: string;

    /**
     * Health statuses of the feed's upstream sources.
     * Once feed settings gets ported to redux, upstream status should live there but it's fine here for now.
     */
    upstreamHealthStatuses?: UpstreamHealthStatus[];
}

export interface IFeedHubUiState {
    isPublic: boolean;
    isAnonymous: boolean;
    showCreateFeed: boolean;
    showIvyPromote?: IPackageVersion;
    showGlobalSettings: boolean;
    errorMessage: IError | undefined;
    dialogData: FeedDialogData | undefined;
}

export interface IFeedHubDataAwareState {
    data: IFeedHubDataState;
}

export interface IFeedHubUiAwareState {
    ui: IFeedHubUiState;
}

export interface IFeedHubViewState extends IFeedHubDataAwareState, IFeedHubUiAwareState {}

export type IFeedHubViewAwareState = IFeedHubViewState & IFeedNavigationAwareState & ITTIAwareState;

export type FeedHubData = WebPageData & {
    isPublic: boolean;
    isAnonymous: boolean;
    packagingUrl: string;
    sessionTokenUri: Uri;
};

export type FeedDialogData = {
    headerText: string;
    dialogBody?: React.ReactFragment;
    className?: string;
    warningBody?: React.ReactFragment;
    primaryButtonText?: string;
    primaryButtonLoadingText?: string;
    primaryButtonClassName?: string;
    primaryButtonDanger?: boolean;
    isLoading?: boolean;
    primaryButtonCallback?: () => void;
    onDismissCallback: () => void;
    dismissButtonText?: string;
};
